/**
 * Add-to-home-screen prompt.
 *
 * On Android/desktop Chromium we capture `beforeinstallprompt` and offer an
 * install button. iOS Safari has no such event, so we show a short "Share →
 * Add to Home Screen" hint instead. Dismissal is remembered in localStorage.
 */

import { isIOS } from './platform';

const DISMISS_KEY = 'trip-planner:install-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function remember(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* ignore storage failures (private mode) */
  }
}

export function initInstallPrompt(
  banner: HTMLElement,
  installBtn: HTMLButtonElement,
  dismissBtn: HTMLButtonElement,
  textEl: HTMLElement,
): void {
  if (isStandalone() || wasDismissed()) return;

  let deferred: BeforeInstallPromptEvent | null = null;

  const hide = (): void => {
    banner.hidden = true;
  };
  const show = (): void => {
    banner.hidden = false;
  };

  dismissBtn.addEventListener('click', () => {
    remember();
    hide();
  });

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    installBtn.hidden = false;
    show();
  });

  installBtn.addEventListener('click', async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice.catch(() => undefined);
    deferred = null;
    hide();
  });

  window.addEventListener('appinstalled', () => {
    remember();
    hide();
  });

  // iOS: no beforeinstallprompt — guide the user manually.
  if (isIOS()) {
    textEl.textContent = 'להתקנה: שתפו ⬆︎ ואז "הוסף למסך הבית"';
    installBtn.hidden = true;
    show();
  }
}
